<script>
/* COMPOSANTS */
import GaView from '@components/v2/layout/GaView';
import GaListView from '@components/v2/layout/GaListView';
import AkListFilter from '@components/v2/filter/AkListFilter';
import AkAllFilter from '@components/v2/filter/AkAllFilter';
import AkDatatablePagination from '@components/v2/general/AkDatatablePagination';
import AkDatatable from '@components/v2/general/AkDatatable.vue';
import AkAutocompleteFilter from '@components/v2/filter/AkAutocompleteFilter';

/* MIXINS */
import utilsMixin from '@mixins/utilsMixin';
import randomRef from '@mixins/randomRef';
import permissionsMixinComputed from '@mixins/permissionsMixinComputed';
import sidebarMixin from '@mixins/sidebarMixin';
import loaderMixin from '@mixins/loaderMixin';
import messageMixin from '@mixins/messageMixin';
import reviewMixin from '@mixins/reviewMixin';
import filterListMixin from '@mixins/filterListMixin';
import companyMixin from '@mixins/companyMixin';
import ownerAutocompleteMixin from '@mixins/autocomplete/ownerAutocompleteMixin';
import reservationSiteConst from '@mixins/const/reservationSiteConst';

/* SERVICES */
import reviewService from '@services/reviewService';
import AkButtonAction from '@components/v2/general/AkButtonAction.vue';
import {useFilterStore} from '@store/filterStore';
import MainTabMixin from '@mixins/mainTabMixin';
import GaLayoutEmptyTab from '@components/v2/general/GaLayoutEmptyTab.vue';
import AkButtonValidation from '@components/v2/general/AkButtonValidation.vue';

export default {
  components: {
    AkButtonValidation,
    GaLayoutEmptyTab,
    AkButtonAction,
    AkAllFilter,
    AkAutocompleteFilter,
    AkDatatablePagination,
    AkDatatable,
    AkListFilter,
    GaListView,
    GaView,
  },
  mixins: [
    utilsMixin,
    randomRef,
    sidebarMixin,
    loaderMixin,
    messageMixin,
    permissionsMixinComputed,
    reviewMixin,
    filterListMixin,
    ownerAutocompleteMixin,
    companyMixin,
    reservationSiteConst,
    MainTabMixin,
  ],
  setup() {
    return {};
  },
  head() {
    return {
      title: 'review.title',
    };
  },
  data() {
    return {
      loading: true,
      filter: {
        page: 1,
        limit: 20,
        sort: ['submitted_at:DESC'],
      },
      list: [],
      stats: [],
      nbItems: 0,
      genericSortHousekeeper: [{field: 'submittedAt', order: -1}],
    };
  },
  mounted() {
    let filter = useFilterStore().getFilter();
    if (filter) {
      this.filter = {...this.filter, ...filter};
    }
  },
  computed: {
    isEmptyList() {
      return !this.list || this.list.length === 0;
    },
  },
  methods: {
    search() {
      this.loading = true;
      reviewService
        .reviewsPagination(this.filter)
        .then((data) => {
          this.list = data.data.list;
          this.stats = data.data.stats;
          this.nbItems = data.items;
        })
        .finally(() => (this.loading = false));
    },

    // FILTER

    openFilterPanel() {
      this.toggleFilterTitle(
        'GaReviewListFilter',
        this.$t('filter_all'),
        this.filter,
        this.clickSearch,
        this.resetFilter,
      );
    },
    resetFilter() {
      this.filter = {
        page: 1,
        limit: 20,
        sort: ['submitted_at:DESC'],
      };
      this.toggleFilterTitle(
        'GaReviewListFilter',
        this.$t('filter_all'),
        this.filter,
        this.clickSearch,
        this.resetFilter,
      );
    },

    // SIDEBAR

    rowClick(event) {
      if (event.originalEvent?.target?.className?.includes('localizer')) {
        this.toggleReservationSidebar(event);
      } else {
        let data = event.data;
        this.toggleSidebar('GaReviewSidebar', this.$t('review.details'), data.id, this.toggleReservationSidebar);
      }
    },

    // SIDEBAR RESERVATION

    toggleReservationSidebar(event) {
      let data = event.data;
      data.header = data.reservationNum;
      this.toggleSidebar('GaReservationSidebar', 'Détails de la réservation', data.reservationId);
    },

    // EXPORT

    exportList() {
      this.showTotalLoaderWithAfter(this.$t('export_in_progress'));
      reviewService
        .exportList(this.filter)
        .then((data) => {})
        .catch((e) => {
          let message = this.msgForErr(this.$t, e);
          this.addError(message);
        })
        .finally(() => {
          this.hideLoader();
        });
    },
  },
};
</script>

<template>
  <GaView v-show="hasAccommodation || !isOwner">
    <template #action>
      <div class="flex justify-end gap-4">
        <AkButtonAction
          secondary
          :label="$t('payment.download')"
          :disabled="this.isEmptyList"
          little
          @click="exportList()" />
      </div>
    </template>

    <template #content>
      <GaListView>
        <template #tabs>
          <div class="grid grid-cols-2 md:flex gap-2">
            <div
              class="border border-grey-50 rounded-lg py-2 px-2 group-md:hover:border-secondary min-w-[120px]"
              v-for="item of this.stats"
              :key="item.reservationSite">
              <div class="flex items-center gap-4">
                <span
                  class="logo-site"
                  :class="iconReservation(item.reservationSite)"
                  v-tooltip.bottom="displayReservationSite(item.reservationSite)" />
                <div class="flex flex-col">
                  <span
                    class="font-inter font-medium text-medium"
                    :class="this.colorRatingWithRating(item.reservationSite, item.globalRating)">
                    {{ this.formatAmount(item.globalRating) }} / {{ item.scale }}
                  </span>
                  <span class="font-inter text-grey-400 font-medium text-sm">{{ item.nbTotalReviews }} avis</span>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template #filter>
          <AkListFilter
            v-if="this.isGas"
            :multi-selection="true"
            v-model="this.filter.companyIds"
            :items="this.companies"
            :display-filter="true"
            @search="this.clickSearch"
            :label="$t('review.company_name')"
            direction="right" />
          <AkAutocompleteFilter
            v-if="!isOwner"
            v-model="this.filter.ownerId"
            @search="this.clickSearch"
            :label="$t('role.owner')"
            item-value="id"
            item-display="display"
            direction="right"
            :init-method="this.autocompleteOwnerById"
            :search-method="this.autocompleteOwner"
            :search-object="this.autocompleteOwnerCustomer()"
            :search-label="this.autocompleteOwnerQuery" />
          <AkAllFilter @open="this.openFilterPanel" />
        </template>

        <template #content>
          <AkDatatablePagination
            v-if="!this.isHousekeeper"
            :loading="loading"
            :value="list"
            :lazy="true"
            :nb-items="this.nbItems"
            :filter="this.filter"
            @row-click="rowClick($event)"
            @search="this.search">
            <template #empty>
              {{ $t('review.empty') }}
            </template>

            <Column
              :header="$t('reservation.reservation_site')"
              header-class="text-center"
              style="width: 80px; text-align: center">
              <template #body="slotProps">
                <span
                  class="logo-site"
                  :class="iconReservation(slotProps.data.reservationSite)"
                  v-tooltip.bottom="displayReservationSite(slotProps.data.reservationSite)" />
              </template>
            </Column>

            <Column
              :header="$t('company.one')"
              v-if="this.isGas"
              style="width: 200px; min-width: 200px"
              :sortable="true"
              sort-field="j_company_name">
              <template #body="slotProps">
                {{ slotProps.data.companyName }}
              </template>
            </Column>
            <Column :header="$t('owner.ownerDisplay')" style="min-width: 100px; width: 100px">
              <template #body="slotProps">
                <span v-if="this.isPartenaire">
                  {{ slotProps.data.ownerDisplay }}
                </span>
                <router-link
                  v-else
                  class="whitespace-pre-wrap"
                  :to="{
                    name: 'ownerPage',
                    params: {
                      ownerId: slotProps.data.ownerId,
                    },
                  }">
                  {{ slotProps.data.ownerDisplay }}
                </router-link>
              </template>
            </Column>

            <Column
              :header="$t('accommodation.short')"
              style="min-width: 300px; width: 300px"
              :sortable="true"
              sort-field="j_accommodation_name">
              <template #body="slotProps">
                <router-link
                  v-if="this.canAccessAccommodation(slotProps.data.accommodationId)"
                  :to="{name: 'accommodationPage', params: {accommodationId: slotProps.data.accommodationId}}">
                  {{ slotProps.data.accommodationName }}
                </router-link>
                <span v-else class="text-base">
                  {{ slotProps.data.accommodationName }}
                </span>
              </template>
            </Column>

            <Column
              :header="$t('reservation.one')"
              style="min-width: 300px; width: 300px"
              :sortable="true"
              sort-field="j_reservation_num">
              <template #body="slotProps">
                <router-link
                  v-if="this.isGasGav"
                  :to="{
                    name: 'reservationPage',
                    params: {
                      reservationId: slotProps.data.reservationId,
                    },
                  }">
                  {{ slotProps.data.reservationNum }}
                </router-link>
                <span
                  v-else
                  class="text-secondary font-semibold text-base cursor-pointer hover:text-secondary localizer"
                  @click="toggleReservationSidebar(slotProps)">
                  {{ slotProps.data.reservationNum }}
                </span>
              </template>
            </Column>

            <Column :header="$t('review.name')" style="min-width: 100px; width: 100px">
              <template #body="slotProps">
                <span class="whitespace-pre-wrap">
                  {{ slotProps.data.name }}
                </span>
              </template>
            </Column>

            <Column
              :header="$t('review.rating_ten')"
              :sortable="true"
              field="rating_ten"
              style="min-width: 100px; width: 100px">
              <template #body="slotProps">
                <span :class="this.colorRating(slotProps.data)">
                  {{ this.formatAmount(this.rating(slotProps.data)) }} /
                  {{ this.scaleRating(slotProps.data.reservationSite) }}
                </span>
              </template>
            </Column>
            <Column
              :header="$t('review.submitted_at')"
              style="width: 150px; min-width: 150px"
              :sortable="true"
              sort-field="submitted_at">
              <template #body="slotProps">
                {{ this.formatDateIfNeeded(slotProps.data.submittedAt) }}
              </template>
            </Column>
          </AkDatatablePagination>

          <AkDatatable
            v-else
            :loading="loading"
            :value="list"
            :paginator="true"
            :multiSortMeta="this.genericSortHousekeeper"
            @row-click="rowClick($event)"
            @search="this.search">
            <template #empty>
              {{ $t('review.empty') }}
            </template>

            <Column
              :header="$t('reservation.reservation_site')"
              header-class="text-center"
              style="width: 80px; text-align: center">
              <template #body="slotProps">
                <span
                  class="logo-site"
                  :class="iconReservation(slotProps.data.reservationSite)"
                  v-tooltip.bottom="displayReservationSite(slotProps.data.reservationSite)" />
              </template>
            </Column>

            <Column
              :header="$t('company.one')"
              v-if="this.isGas"
              style="width: 200px; min-width: 200px"
              :sortable="true"
              field="companyName">
              <template #body="slotProps">
                {{ slotProps.data.companyName }}
              </template>
            </Column>
            <Column :header="$t('owner.ownerDisplay')" style="min-width: 100px; width: 100px">
              <template #body="slotProps">
                <span v-if="this.isPartenaire">
                  {{ slotProps.data.ownerDisplay }}
                </span>
                <router-link
                  v-else
                  class="whitespace-pre-wrap"
                  :to="{
                    name: 'ownerPage',
                    params: {
                      ownerId: slotProps.data.ownerId,
                    },
                  }">
                  {{ slotProps.data.ownerDisplay }}
                </router-link>
              </template>
            </Column>

            <Column
              :header="$t('accommodation.short')"
              style="min-width: 300px; width: 300px"
              :sortable="true"
              field="accommodationName">
              <template #body="slotProps">
                <router-link
                  v-if="this.canAccessAccommodation(slotProps.data.accommodationId)"
                  :to="{name: 'accommodationPage', params: {accommodationId: slotProps.data.accommodationId}}">
                  {{ slotProps.data.accommodationName }}
                </router-link>
                <span v-else class="text-base">
                  {{ slotProps.data.accommodationName }}
                </span>
              </template>
            </Column>

            <Column
              :header="$t('reservation.one')"
              style="min-width: 300px; width: 300px"
              :sortable="true"
              field="reservationNum">
              <template #body="slotProps">
                <router-link
                  v-if="this.isGasGav"
                  :to="{
                    name: 'reservationPage',
                    params: {
                      reservationId: slotProps.data.reservationId,
                    },
                  }">
                  {{ slotProps.data.reservationNum }}
                </router-link>
                <span
                  v-else
                  class="text-secondary font-semibold text-base cursor-pointer hover:text-secondary localizer"
                  @click="toggleReservationSidebar(slotProps)">
                  {{ slotProps.data.reservationNum }}
                </span>
              </template>
            </Column>

            <Column :header="$t('review.name')" style="min-width: 100px; width: 100px">
              <template #body="slotProps">
                <span class="whitespace-pre-wrap">
                  {{ slotProps.data.name }}
                </span>
              </template>
            </Column>

            <Column
              :header="$t('review.rating_ten')"
              :sortable="true"
              field="ratingTen"
              style="min-width: 100px; width: 100px">
              <template #body="slotProps">
                <span :class="this.colorRating(slotProps.data)">
                  {{ this.formatAmount(this.rating(slotProps.data)) }} /
                  {{ this.scaleRating(slotProps.data.reservationSite) }}
                </span>
              </template>
            </Column>
            <Column
              :header="$t('review.submitted_at')"
              style="width: 150px; min-width: 150px"
              :sortable="true"
              field="submittedAt">
              <template #body="slotProps">
                {{ this.formatDateIfNeeded(slotProps.data.submittedAt) }}
              </template>
            </Column>
          </AkDatatable>
        </template>
      </GaListView>
    </template>
  </GaView>
  <ga-layout-empty-tab
    v-if="!this.hasAccommodation && isOwner"
    icon="ga-stars"
    title="Mes __avis__"
    gradient="linear-gradient(278deg, rgba(225, 155, 58, 0.20) 0.43%, rgba(255, 255, 255, 0.20) 44.56%), linear-gradient(104deg, rgba(225, 155, 58, 0.20) 1.44%, rgba(255, 255, 255, 0.00) 54.09%), linear-gradient(0deg, #FFF7E5 0%, #FFF7E5 100%)"
    background-color="#FFF7E5"
    @init="init">
    <div class="flex flex-col grow items-center md:gap-8 gap-4 md:p-4 p-8 pb-0 md:min-w-[45%]">
      <div class="border-[7px] border-[#FFE197] rounded-full flex items-center justify-center w-[70px] h-[70px]">
        <i class="ga-icon ga-stars text-[30px] text-grey-900" />
      </div>
      <div class="flex flex-col gap-6 items-center">
        <div class="text-base text-grey-900 text-center">
          <div class="font-semibold text-lg">Retrouvez ici vos avis !</div>
          Publiez votre logement sur Guestadom et générez vos premiers avis !
        </div>
        <AkButtonValidation
          class="btn-primary !h-[48px] w-fit !px-8 font-medium"
          @click="goToOnboarding"
          ref="onboardingButton"
          :label="hasPendingOnboarding ? 'Reprendre la publication' : 'Démarrer la publication'" />
      </div>
    </div>
    <img v-if="$isMobile()" src="@refonte/img/emptyTabs/reviews_mobile_illustration.png" alt="" class="w-full" />
    <div v-else class="h-full md:max-w-[55%] w-full flex items-end justify-end">
      <img src="@refonte/img/emptyTabs/reviews_illustration.png" alt="" class="h-auto object-cover object-left" />
    </div>
  </ga-layout-empty-tab>
</template>
